import React from 'react';
import { DateTime } from 'luxon';

import ClickableDiv from 'ds/ClickableDiv';
import Layout from 'ds/Layout';
import Text from 'ds/Text';
import { GRAY_50_HEX } from 'shared';
import { CleaningServiceCalendarDate } from 'store/CleaningService/types';

import CalendarDateDisplay from './CalendarDateDisplay';
import CalendarDateTaskGroupDisplay from './CalendarDateTaskGroupDisplay';

interface Props {
  calendarDate: CleaningServiceCalendarDate;
  month: number;
  roundTopLeft?: boolean;
  roundTopRight?: boolean;
  roundBottomLeft?: boolean;
  roundBottomRight?: boolean;
  onClick?: (calendarDate: CleaningServiceCalendarDate) => void;
}

const CalendarDate: React.FC<Props> = ({
  calendarDate,
  month,
  roundTopLeft,
  roundTopRight,
  roundBottomLeft,
  roundBottomRight,
  onClick
}) => {
  const { date, summary, tasks } = calendarDate;
  const styleProps: React.CSSProperties = {};
  const isFirstOfWeek = date.weekday === 1;
  const isLastOfWeek = date.weekday === 7;
  const isFirstWeekOfMonth = date.weekNumber === DateTime.fromObject({ month }).startOf('month').weekNumber;
  const isLastWeekOfMonth = date.weekNumber === DateTime.fromObject({ month }).endOf('month').weekNumber;
  const dateHasTask = summary && tasks.length;

  if ((isFirstOfWeek && isFirstWeekOfMonth) || roundTopLeft) {
    styleProps.borderTopLeftRadius = 16;
  }

  if ((isLastOfWeek && isFirstWeekOfMonth) || roundTopRight) {
    styleProps.borderTopRightRadius = 16;
  }

  if ((isFirstOfWeek && isLastWeekOfMonth) || roundBottomLeft) {
    styleProps.borderBottomLeftRadius = 16;
  }

  if ((isLastOfWeek && isLastWeekOfMonth) || roundBottomRight) {
    styleProps.borderBottomRightRadius = 16;
  }

  return (
    <ClickableDiv
      onClick={() => onClick?.(calendarDate)}
      style={{
        border: `1px solid ${GRAY_50_HEX}`,
        padding: 16,
        ...styleProps
      }}
      className="CleaningServiceCalendarDateDisplay"
    >
      {isFirstWeekOfMonth && (
        <Layout justify="center">
          <Layout>
            <Text size="body-xs">{date.weekdayShort}</Text>
          </Layout>
        </Layout>
      )}
      <Layout height={96} paddingTop={4} direction="column" align="center">
        <CalendarDateDisplay date={date} />
        {dateHasTask ? (
          <Layout marginTop={16} width="100%">
            <CalendarDateTaskGroupDisplay date={date} summary={summary} tasks={tasks} />
          </Layout>
        ) : null}
      </Layout>
    </ClickableDiv>
  );
};

export default CalendarDate;
